import { Button, DatePicker, Divider, Flex, Popover, Select } from "antd"
import { type RangePickerProps } from "antd/lib/date-picker"
import dayjs from "dayjs"
import React, { useState } from "react"

import Icon from "../../Icon"
import { cn } from "../../utils/cn.ts"
import { DATE_FORMAT } from "../../utils/date/date.ts"

const { RangePicker } = DatePicker

export interface DateRangePreset {
  key: string
  label: string
}

interface PresetDateRangeFilterProps<T> {
  presets: readonly DateRangePreset[]
  preset?: string | null
  after?: string | null
  before?: string | null
  /** The three params this control owns: a preset key, or a custom pair. */
  presetKey: keyof T
  afterKey: keyof T
  beforeKey: keyof T
  label: string
  placeholder?: string
  allTimeLabel: string
  customLabel: string
  className?: string
  onChange: (patch: Partial<T>) => void
}

/**
 * One closed `Select` trigger opening a list of period presets and a custom
 * range. A preset is sent by key and resolved on the server, so every surface
 * that offers "Last Month" means the same days; only a custom range carries
 * dates. Choosing either kind clears the other.
 */
export function PresetDateRangeFilter<T>({
  presets,
  preset,
  after,
  before,
  presetKey,
  afterKey,
  beforeKey,
  label,
  placeholder,
  allTimeLabel,
  customLabel,
  className = "basis-60 w-60 max-md:grow",
  onChange
}: PresetDateRangeFilterProps<T>) {
  const [open, setOpen] = useState(false)
  const [customOpen, setCustomOpen] = useState(false)

  const hasRange = !!after && !!before
  const activePreset = presets.find((option) => option.key === preset) ?? null

  const triggerLabel = activePreset
    ? activePreset.label
    : hasRange
      ? `${dayjs(after).format(DATE_FORMAT)} - ${dayjs(before).format(DATE_FORMAT)}`
      : null

  const emit = (
    nextPreset: string | null,
    start: string | null,
    end: string | null
  ) =>
    onChange({
      [presetKey]: nextPreset,
      [afterKey]: start,
      [beforeKey]: end
    } as Partial<T>)

  const close = () => {
    setCustomOpen(false)
    setOpen(false)
  }

  const onRangeChange: RangePickerProps["onChange"] = (_dates, dateStrings) => {
    emit(null, dateStrings?.[0] || null, dateStrings?.[1] || null)
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (!next) setCustomOpen(false)
      }}
      trigger={"click"}
      content={
        <Flex vertical gap={2} className={"w-44"}>
          <Button
            type={!activePreset && !hasRange ? "primary" : "text"}
            size={"small"}
            className={"justify-start text-left"}
            onClick={() => {
              emit(null, null, null)
              close()
            }}
          >
            {allTimeLabel}
          </Button>
          {presets.map((option) => (
            <Button
              key={option.key}
              type={activePreset?.key === option.key ? "primary" : "text"}
              size={"small"}
              className={"justify-start text-left"}
              onClick={() => {
                emit(option.key, null, null)
                close()
              }}
            >
              {option.label}
            </Button>
          ))}
          <Button
            type={!activePreset && hasRange ? "primary" : "text"}
            size={"small"}
            className={"justify-start text-left"}
            onClick={() => setCustomOpen(true)}
          >
            {customLabel}
          </Button>
          {customOpen && (
            <>
              <Divider className={"my-1"} />
              <RangePicker
                autoFocus
                value={hasRange ? [dayjs(after), dayjs(before)] : undefined}
                onChange={onRangeChange}
                format={DATE_FORMAT}
              />
            </>
          )}
        </Flex>
      }
    >
      <Select
        allowClear
        aria-label={label}
        open={false}
        prefix={<Icon type={"Calendar"} />}
        value={triggerLabel}
        placeholder={placeholder ?? label}
        onClear={() => emit(null, null, null)}
        className={cn(className, "group justify-start")}
      />
    </Popover>
  )
}
